import {
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Link,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import createServiceProfile from 'API/ServiceProfiles/createServiceProfile';
import updateServiceProfiles from 'API/ServiceProfiles/updateServiceProfiles';
import useFieldValidation from 'hooks/useFieldValidation';
import { toSentenceCase } from 'utils/utils';
import LoadingIndicator from 'components/v4/LoadingIndicator';
import FormBase from '@/components/forms/new/FormBase';
import { validationSchemaEightByEight as validationSchema } from './schema';
import ServiceProfileVisibleScope from './formFields/ServiceProfileVisibleScope';
import EightByEightServiceProviderField from './formFields/EightByEightServiceProviderField';
import EightByEightLicenceTree from './formFields/EightByEightLicenceTree';
import { breadCrumbStyling } from 'components/forms/new/formStyles';
import getEightByEightServiceProviders from 'API/ServiceProfiles/getEightByEightServiceProviders';
import getEightByEightCatalogue from 'API/ServiceProfiles/getEightByEightCatalogue';
import { toCatalogItemPayload } from 'utils/eightByEightCatalog';
import { getEightByEightServiceProfile } from 'API/ServiceProfiles/getEightByEightServiceProfile';
import { useQuery } from 'hooks/useCustomQuery';

// Used as the catalogItem serviceProfileID when creating a profile that does
// not exist yet — the BE expects an empty GUID rather than null/undefined.
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const EightByEight = ({
    mode,
    data,
    handleBackButton,
    level,
    visibleScopeOptions,
}) => {
    const {
        currentWholesaler: wholesalerID,
        currentPartner: partnerID,
        currentWholesalerData,
        companiesAndPartners,
    } = useSelector((state) => state.navigationLists);
    const { requiredScope } = useSelector((state) => state.login);
    const { darkMode } = useSelector((state) => state.settings);

    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const resolvedPartnerID = data?.partnerID || partnerID;

    const DEFAULT_EIGHTBYEIGHT_PAYLOAD = {
        name: '8x8 Wholesaler Profile',
        serviceType: 'EightByEight',
        partnerID: level === 'partner' ? partnerID : undefined,
        wholesalerID: level === 'partner' ? undefined : wholesalerID,
        visibleScope: level === 'partner' ? 20 : 60,
        serviceProviderID: null,
        catalogItems: [],
    };

    useEffect(() => {
        if (
            mode !== 'EDIT' &&
            level !== 'partner' &&
            currentWholesalerData?.name
        ) {
            handleChangeState({
                name: `${currentWholesalerData?.name} - 8x8 Wholesaler Profile`,
            });
        } else if (
            mode !== 'EDIT' &&
            level === 'partner' &&
            companiesAndPartners?.length > 0
        ) {
            const partnerName = companiesAndPartners.find(
                ({ id }) => id === partnerID,
            )?.name;
            handleChangeState({
                name: `${partnerName} - 8x8 Partner Profile`,
            });
        }
    }, [
        mode,
        companiesAndPartners,
        currentWholesalerData?.name,
        level,
        partnerID,
        wholesalerID,
    ]);

    const [modalState, setModalState] = useState(() => {
        const base = data || DEFAULT_EIGHTBYEIGHT_PAYLOAD;
        // data.catalogItems from the API has shape [{ serviceProfileID, catalogItemID, catalogItem }]
        // — extract the nested catalogItem objects so modalState.catalogItems is always [catalogItem]
        const catalogItems =
            data?.catalogItems?.map((ci) => ci.catalogItem).filter(Boolean) ??
            [];
        return { ...base, catalogItems };
    });

    const { data: serviceProviderOptions = [], isLoading: isLoadingProviders } =
        useQuery({
            queryKey: [
                'eightByEightServiceProviders',
                level,
                resolvedPartnerID,
            ],
            queryFn: () =>
                getEightByEightServiceProviders({
                    level,
                    partnerID: resolvedPartnerID,
                }).then((items) =>
                    items.map((p) => ({ value: p.id, label: p.name })),
                ),
        });

    const { data: catalogue = [], isLoading: isLoadingCatalogue } = useQuery({
        queryKey: ['eightByEightLicences', level, resolvedPartnerID],
        queryFn: () =>
            getEightByEightCatalogue({
                level,
                partnerID: resolvedPartnerID,
            }).then((result) => result.data || []),
    });

    const { data: fullProfile, isLoading: isLoadingProfile } = useQuery({
        queryKey: [
            'eightByEightProfile',
            level,
            wholesalerID,
            resolvedPartnerID,
        ],
        queryFn: () =>
            getEightByEightServiceProfile({
                level,
                wholesalerID,
                partnerID: resolvedPartnerID,
            }),
        enabled: mode === 'EDIT',
    });

    useEffect(() => {
        if (!fullProfile || !catalogue.length) return;
        const catalogItems = (fullProfile.catalogItems || [])
            .map(
                (ci) =>
                    ci.catalogItem ??
                    catalogue.find((c) => c.id === ci.catalogItemID),
            )
            .filter(Boolean);
        setModalState((prev) => ({ ...prev, ...fullProfile, catalogItems }));
    }, [fullProfile, catalogue]);

    // Partner profiles inherit a single service provider from the wholesaler profile
    useEffect(() => {
        if (serviceProviderOptions.length !== 1) return;
        const onlyID = serviceProviderOptions[0].value;
        const currentIsValid = serviceProviderOptions.some(
            (o) => o.value === modalState.serviceProviderID,
        );
        if (!currentIsValid) {
            setModalState((prev) => ({ ...prev, serviceProviderID: onlyID }));
        }
    }, [serviceProviderOptions, modalState.serviceProviderID]);

    const { validatefn } = useFieldValidation(modalState, validationSchema);

    const handleChangeState = (payload) => {
        setModalState((prev) => ({
            ...prev,
            ...payload,
        }));
    };

    const isLoading =
        isLoadingProviders ||
        isLoadingCatalogue ||
        (mode === 'EDIT' && isLoadingProfile);

    if (isLoading) {
        return <LoadingIndicator />;
    }

    const initialValues = {
        name: modalState?.name || '',
        serviceType: 'EightByEight',
        partnerID:
            level === 'partner'
                ? (modalState?.partnerID ?? partnerID)
                : undefined,
        wholesalerID:
            level === 'partner'
                ? undefined
                : (modalState?.wholesalerID ?? wholesalerID),
        visibleScope:
            modalState?.visibleScope ?? (level === 'partner' ? 20 : 60),
        serviceProviderID: modalState?.serviceProviderID ?? null,
        catalogItems: modalState?.catalogItems ?? [],
    };

    // Wholesaler EightByEight requires Global Admin scope (80); partner uses the user's current scope.
    const saveScope = level === 'partner' ? requiredScope : 80;

    const finalFormSubmit = async (values, actions) => {
        setSubmitted(true);
        setIsSaving(true);
        setError(null);
        try {
            if (!validatefn(values)) {
                setError(
                    'There are some validation errors in the form. Please check and try again.',
                );
                actions.setSubmitting(false);
                setIsSaving(false);
                return;
            }
            if (!modalState.serviceProviderID) {
                setError('Please select a service provider.');
                actions.setSubmitting(false);
                setIsSaving(false);
                return;
            }
            const profileID = data?.id || modalState?.id;
            const catalogItems = (modalState.catalogItems || []).map(
                (item) => ({
                    serviceProfileID: profileID || EMPTY_GUID,
                    catalogItemID: item.id,
                    // Strip internal-only fields (rule, parentId, offeringId,
                    // visibility, parentName) so the payload matches the backend
                    // catalogItem schema exactly.
                    catalogItem: toCatalogItemPayload(item),
                }),
            );
            const payload = {
                ...(profileID ? { id: profileID } : {}),
                name: values.name || modalState.name,
                serviceProviderID: modalState.serviceProviderID,
                visibleScope: modalState.visibleScope ?? values.visibleScope,
                // wholesaler profiles require wholesalerID; partner profiles
                // require partnerID. Omitting these causes a 403 on save.
                ...(level === 'partner'
                    ? { partnerID: resolvedPartnerID }
                    : { wholesalerID }),
                catalogItems,
            };
            if (mode === 'EDIT') {
                await updateServiceProfiles({
                    type: 'EightByEight',
                    payload,
                    level,
                    scope: saveScope,
                });
            } else {
                await createServiceProfile({
                    type: 'EightByEight',
                    payload,
                    level,
                    scope: saveScope,
                });
            }
            toast.success(
                `8x8 Service Profile ${mode === 'EDIT' ? 'updated' : 'created'} successfully`,
            );
            handleBackButton();
        } catch (e) {
            setError(
                e?.response?.data?.message ||
                    e?.message ||
                    'An error occurred. Please try again later.',
            );
        } finally {
            setIsSaving(false);
            actions.setSubmitting(false);
        }
    };

    const breadCrumbTrail = [
        { label: 'Services', path: ['', { index: 0 }] },
        { label: 'Configurations', path: '' },
    ];

    return (
        <>
            <Breadcrumb sx={breadCrumbStyling}>
                {breadCrumbTrail?.map((crumb, index) => (
                    <BreadcrumbItem key={index}>
                        {Array.isArray(crumb.path) ? (
                            <BreadcrumbLink
                                as={Link}
                                to={crumb.path[0]}
                                state={crumb.path[1]}
                                onClick={
                                    index === 0 ? handleBackButton : undefined
                                }>
                                {crumb.label}
                            </BreadcrumbLink>
                        ) : (
                            <BreadcrumbLink
                                as={Link}
                                to={crumb.path}
                                onClick={
                                    index === 0
                                        ? () => handleBackButton
                                        : undefined
                                }>
                                {crumb.label}
                            </BreadcrumbLink>
                        )}
                    </BreadcrumbItem>
                ))}
            </Breadcrumb>
            <FormBase
                header={`${toSentenceCase(mode)} ${modalState.name || data?.name || ''}`}
                subTitleHeader={true}
                backLink={['/service-profiles', { state: { index: 0 } }]}
                ufBackBtn="builderBackBtn"
                ufSaveBtn="builderSaveBtn"
                initialValues={initialValues}
                finalFormSubmit={finalFormSubmit}
                customClick={true}
                onBack={handleBackButton}
                isLoading={isSaving}
                enableReinitialize={true}>
                {({ errors = {}, touched = {} }) =>
                    [
                        <ServiceProfileVisibleScope
                            key="eightByEightServiceProfileVisibleScope"
                            name="visibleScope"
                            options={visibleScopeOptions}
                            darkMode={darkMode}
                            minWidth="420px"
                            maxWidth="520px"
                            width="100%"
                            errors={errors}
                            touched={touched}
                            onChange={(e) => {
                                setModalState((prev) => ({
                                    ...prev,
                                    visibleScope: e.value,
                                }));
                            }}
                        />,
                        <EightByEightServiceProviderField
                            key="eightByEightServiceProvider"
                            value={modalState.serviceProviderID}
                            options={serviceProviderOptions}
                            darkMode={darkMode}
                            onChange={(val) =>
                                handleChangeState({ serviceProviderID: val })
                            }
                            error={
                                submitted && !modalState.serviceProviderID
                                    ? 'Please select a service provider'
                                    : ''
                            }
                            showError={submitted}
                        />,
                        <EightByEightLicenceTree
                            key="eightByEightLicences"
                            catalogue={catalogue}
                            value={catalogue.map((item) => ({
                                itemID: item.id,
                                status: (modalState.catalogItems || []).some(
                                    (ci) => ci.id === item.id,
                                )
                                    ? 'Enabled'
                                    : 'Disabled',
                            }))}
                            onChange={(val) => {
                                const enabledItems = val
                                    .filter(
                                        ({ status }) => status === 'Enabled',
                                    )
                                    .map(({ itemID }) =>
                                        catalogue.find((c) => c.id === itemID),
                                    )
                                    .filter(Boolean);
                                handleChangeState({
                                    catalogItems: enabledItems,
                                });
                            }}
                            darkMode={darkMode}
                        />,
                        error && (
                            <Box
                                paddingBottom="0.5rem"
                                key="error-box-eightbyeight">
                                <Alert
                                    key="error"
                                    status="error"
                                    mt={4}
                                    marginBottom="1rem">
                                    <AlertIcon />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            </Box>
                        ),
                    ].filter(Boolean)
                }
            </FormBase>
        </>
    );
};

export default EightByEight;
